/**
 * DirectivePreviewModal - Direktif onizleme modali
 *
 * Direktif gonderilmeden once etkisini gosterir.
 * Kullanici onaylar veya iptal eder.
 */

import { AlertCircle, CheckCircle, X } from 'lucide-react'

interface DirectivePreview {
  directive_type: string
  effect: string
  next_cycle_preview: {
    cycle: number
    added_focus: string
    added_experts: string[]
    budget_change: number
  }
  confirm_required: boolean
}

interface DirectivePreviewModalProps {
  preview: DirectivePreview
  onConfirm: () => void
  onCancel: () => void
}

export default function DirectivePreviewModal({
  preview,
  onConfirm,
  onCancel
}: DirectivePreviewModalProps) {
  const getDirectiveTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      direction_change: 'Yon Degisikligi',
      question: 'Soru',
      add_resource: 'Kaynak Ekleme',
      accelerate: 'Hizlandirma',
      stop: 'Durdurma',
      branch: 'Dal Acma'
    }
    return labels[type] || type
  }

  const getDirectiveTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      direction_change: 'badge-primary',
      question: 'badge-info',
      add_resource: 'badge-success',
      accelerate: 'badge-warning',
      stop: 'badge-danger',
      branch: 'badge-secondary'
    }
    return colors[type] || 'badge-secondary'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="card max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="card-header flex items-center justify-between">
          <h3 className="card-title">Direktif Onizleme</h3>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-opacity-80 rounded transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="card-body space-y-4">
          {/* Direktif Tipi */}
          <div>
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Tip:
            </span>
            <span className={`badge ${getDirectiveTypeColor(preview.directive_type)} ml-2`}>
              {getDirectiveTypeLabel(preview.directive_type)}
            </span>
          </div>

          {/* Etki */}
          <div
            className="p-4 rounded flex items-start gap-3"
            style={{ background: 'var(--card-bg-secondary)' }}
          >
            <AlertCircle size={20} style={{ color: 'var(--warning-color)' }} className="flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Etki
              </h4>
              <p style={{ color: 'var(--text-secondary)' }}>
                {preview.effect}
              </p>
            </div>
          </div>

          {/* Sonraki Cycle Onizleme */}
          {preview.next_cycle_preview && (
            <div
              className="p-4 rounded"
              style={{ background: 'var(--card-bg-secondary)' }}
            >
              <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Sonraki Cycle Degisiklikleri (Cycle {preview.next_cycle_preview.cycle})
              </h4>

              <div className="space-y-3">
                {/* Eklenen Odak */}
                {preview.next_cycle_preview.added_focus && (
                  <div>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Eklenen Odak:
                    </span>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                      {preview.next_cycle_preview.added_focus}
                    </p>
                  </div>
                )}

                {/* Eklenen Uzmanlar */}
                {preview.next_cycle_preview.added_experts.length > 0 && (
                  <div>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Eklenen Uzmanlar:
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {preview.next_cycle_preview.added_experts.map((expert) => (
                        <span key={expert} className="badge badge-success">
                          {expert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Butce Degisikligi */}
                {preview.next_cycle_preview.budget_change !== 0 && (
                  <div>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Butce Degisikligi:
                    </span>
                    <span
                      className="ml-2 font-semibold"
                      style={{
                        color: preview.next_cycle_preview.budget_change > 0
                          ? 'var(--success-color)'
                          : 'var(--danger-color)'
                      }}
                    >
                      {preview.next_cycle_preview.budget_change > 0 ? '+' : ''}
                      {preview.next_cycle_preview.budget_change} FLZ
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onay Gerekli Uyarisi */}
          {preview.confirm_required && (
            <div
              className="p-3 rounded flex items-center gap-2"
              style={{ background: 'var(--warning-bg)' }}
            >
              <CheckCircle size={18} style={{ color: 'var(--warning-color)' }} />
              <p className="text-sm" style={{ color: 'var(--warning-color)' }}>
                Bu degisiklik sonraki cycle&apos;a uygulanacak. Mevcut cycle etkilenmeyecek.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-footer flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="btn btn-secondary"
          >
            Iptal
          </button>
          <button
            onClick={onConfirm}
            className="btn btn-primary"
          >
            Onayla
          </button>
        </div>
      </div>
    </div>
  )
}
